<template>
  <div class="template-component">
    <!-- Add your template content here -->

    <div class="level">
      <div class="level-left">
        <div class="level-item">
          <div :class="['dropdown', { 'is-active': isFilterActive }]">
            <div class="dropdown-trigger">
              <button class="button"
                @click="toggleFilterDropdown">
                <span class="icon has-text-primary">
                  <i class="mdi mdi-magnify mdi-18px" />
                </span>
                <span>Filter</span>
                <span v-if="selectedFilterTags.length"
                  class="ml-2 tag"
                  :class="`is-${$theme.name}`"
                  :style="{ borderRadius: '20px' }">
                  {{ selectedFilterTags.length }}
                </span>
              </button>
            </div>
            <TagsDropdownMenu :tags="tags"
              :selected-tags="selectedFilterTags"
              :is-visible="isFilterActive"
              @toggle-tag="toggleFilterTag"
              @clear-all-tags="clearAllFilterTags" />
          </div>
        </div>
        <div class="level-item"
          v-if="showManageTag">
          <button class="button"
            @click="toggleManageTagsModal">Manage Tags</button>
        </div>
      </div>
    </div>

    <div v-if="templatesLoading"
      style="position: relative; top: 100px">
      <spinner />
    </div>

    <div v-if="templates.length === 0 && !templatesLoading">
      <div class="is-flex py-4 m-1 is-justify-content-center is-align-items-center has-text-grey">
        <div>
          <div class="is-flex is-justify-content-center my-2 has-text-grey-lighter">
            <span class="material-symbols-rounded is-size-1"> note_add </span>
          </div>

          <!-- show a different message depending on if user has permissions to add templates -->
          <span v-if="!!permussions?.hasAdd"
            class="is-flex is-align-items-center is-justify-content-center is-flex-direction-column has-text-centered">
            <span>No templates yet for this vehicle type, but you can create one!</span>
            <span>Just add items to your quote, click More options (top right),</span>
            <span>and select Save Template for future use.</span>
          </span>
          <span v-else
            class="is-flex is-align-items-center is-justify-content-center is-flex-direction-column has-text-centered">
            <span>No templates yet!</span>
            <span>Please contact an admin or support staff to create one.</span>
          </span>
        </div>
      </div>
    </div>

    <div v-else>
      <div class="templates-container">
        <div class="has-text-grey-light is-flex is-flex-direction-column is-align-items-center"
          v-if="filteredTemplates.length === 0">
          <span class="icon">
            <i class="mdi mdi-feature-search-outline mdi-56px" />
          </span>
          <span class="mt-3 pt-3">No results found</span>
        </div>

        <div class="is-flex p-1 m-1 is-justify-content-space-between is-align-items-center template-list-item"
          :class="`quote-builder-template-item-${$theme.name}`"
          v-for="i in filteredTemplates"
          :key="i.templateId">
          <div class="is-flex"
            style="flex: 1; min-width: 0;">
            <div class="ml-2"
              style="min-width: fit-content;">{{ i.name }}</div>
            <div class="tags-container ml-2">
              <span class="tag is-light"
                v-for="tag in getSortedTags(i.tags)"
                :key="tag.tagID"
                :style="{ backgroundColor: tag.colorHex, color: getTextColor(tag.colorHex), borderRadius: '9999px', fontWeight: 'bold' }">
                {{ tag.shortTag }}
              </span>
            </div>
          </div>

          <div class="is-flex is-align-items-center py-1">
            <!-- <button class="button is-ghost is-small p-0 has-text-grey is-clickable mr-1"
              @click="handleTemplateClick(i)">
              <span class="material-symbols-outlined">visibility</span>
            </button> -->

            <button class="button is-ghost is-small tooltip p-0 has-text-primary is-clickable mr-2"
              @click="handleTemplateClick(i)"
              data-tooltip="View and Add Items">
              <span class="material-symbols-outlined">add</span>
            </button>

            <button class="button is-ghost is-small tooltip px-0 mr-1 has-text-grey-lighter"
              @click="handleDeleteTemplateClick(i)"
              :disabled="!permussions?.hasDelete"
              data-tooltip="Delete"
              style="border: none; height: auto; background: transparent">
              <span class="material-symbols-rounded"
                @mouseenter="$event.target.classList.add('has-text-danger')"
                @mouseleave="$event.target.classList.remove('has-text-danger')">
                delete
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <confirm-modal v-if="templateDeleted"
      :active.sync="isConfirmModalActive"
      @ok="deleteTemplate(true)"
      @cancel="deleteTemplate(false)"
      :ok-text="'Yes'"
      :cancel-text="'No'">
      <p slot="text-title">Delete Quote Template</p>
      <p slot="text-content"
        class="has-text-danger">
        Quote Template <span class="has-text-weight-bold">{{ templateDeleted.name }}</span>
        will be deleted. Continue?
      </p>
    </confirm-modal>

    <confirm-modal v-if="templateToAdd"
      :active.sync="addTemplateConfirmModalActive"
      @ok="addItemsFromTemplate"
      @cancel="cancelAddItems"
      :ok-text="'Ok'"
      :cancel-text="'Cancel'">
      <p slot="text-title"
        class="has-text-weight-semibold">Add Items from: {{ templateToAdd.name }}</p>
      <div slot="text-content">
        <p>You are about to import {{ templateItems?.length }} items into Quote {{ quoteNo }}.</p>
        <p class="mt-2">Press OK to confirm or Cancel to go back.</p>

        <p class="mt-3 is-size-6 has-text-grey">
          <span class="has-text-weight-semibold">Note:</span>
          This action cannot be undone.
        </p>
      </div>
    </confirm-modal>

    <template-items-modal v-if="isTemplateItemsModalActive"
      :active.sync="isTemplateItemsModalActive"
      :selected-template="selectedTemplate"
      :tags="tags"
      :rates="rates"
      @addTemplateItemsCustom="(obj) => $emit('addTemplateItemsCustom', obj)" />
    <manage-tags-modal v-if="showManageTag"
      :active="showManageTagsModal"
      :tags="tags"
      :tag-category-id="tagCategoryId"
      @close="showManageTagsModal = false"
      @save="handleSaveTags" />
  </div>
</template>

<script>
import QuoteTemplateService from '@/services/QuoteTemplateService'
import { QuoteTemplateListColumns } from '@/views/quotetemplate/columns.js'
import { QuoteTemplateTypeKeys } from '@/enums/QuoteTemplateTypes'
import TemplateItemsModal from './TemplateItemsModal.vue'
import Spinner from '@/components/Spinners'
import ConfirmModal from '../../../../components/BulmaModal/ConfirmModal.vue'
import { EventHubTypes } from '../../../../enums'
import ManageTagsModal from '../../../manageTag/ManageTagsModal.vue'
import { getTextColor } from '@/utils/ColorFunctions'
import TagsDropdownMenu from '../../../manageTag/TagsDropdownMenu.vue'

export default {
  name: 'TemplateComponent',
  components: {
    TemplateItemsModal,
    Spinner,
    ConfirmModal,
    ManageTagsModal,
    TagsDropdownMenu
  },

  props: {
    vehicleBodyId: {
      type: String,
      required: true
    },
    quoteTimesType: {
      type: String,
      required: true
    },
    rates: {
      type: Array,
      required: true
    },
    quoteNo: {
      type: Number,
      required: true
    },
    tags: {
      type: Array,
      default: () => []
    },
    tagCategoryId: {
      type: String,
      default: null
    },
    showManageTag: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      isTemplateItemsModalActive: false,
      selectedTemplateId: '',
      templatesLoading: false,
      templates: [],
      filter: {
        name: '',
        sortColumn: QuoteTemplateListColumns[0].name,
        sortOrder: QuoteTemplateListColumns[0].defaultOrder,
        pageIndex: 1,
        pageSize: 200
      },

      isConfirmModalActive: false,
      templateDeleted: null,

      addTemplateConfirmModalActive: false,
      templateToAdd: null,
      templateItems: [],
      isFilterActive: false,
      showManageTagsModal: false,

      selectedFilterTags: []
    }
  },
  computed: {
    // Add your computed properties here
    quoteTemplateTypeKeys() {
      return QuoteTemplateTypeKeys
    },
    permussions() {
      return this.$user.permissions?.find((p) => p.codeName === 'A073') || {}
    },
    filteredTemplates() {
      if (!this.selectedFilterTags.length) return this.templates

      return this.templates.filter((template) => template.tags && template.tags.some((tag) => this.selectedFilterTags.includes(tag.tagID)))
    }
  },
  mounted() {
    this.getQuoteTemplates()
    // Add your mounted logic here
    this.$eventHub.$on(EventHubTypes.ReloadQuoteTemplate, () => {
      this.getQuoteTemplates()
    })
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutsideFilter)
  },
  methods: {
    getTextColor,
    handleTemplateClick(template) {
      this.isTemplateItemsModalActive = true
      this.selectedTemplate = template
    },
    async getQuoteTemplate(id) {
      const quoteTemplates = await QuoteTemplateService.getSortedQuoteTemplateItems(id)
      this.templateItems = quoteTemplates
    },
    handleDeleteTemplateClick(template) {
      this.templateDeleted = template
      this.isConfirmModalActive = true
    },
    async deleteTemplate(isDelete = false) {
      if (!isDelete) {
        this.templateDeleted = null
        this.isConfirmModalActive = false
        return
      }
      await QuoteTemplateService.deleteQuoteTemplate(this.templateDeleted)
      this.$nextTick(() => {
        this.getQuoteTemplates()
        this.templateDeleted = null
        this.isConfirmModalActive = false
      })
    },
    handleAddTemplateClick(template) {
      this.templateToAdd = template
      this.addTemplateConfirmModalActive = true
      this.getQuoteTemplate(template.templateId)
    },
    addItemsFromTemplate() {
      this.addTemplateConfirmModalActive = false
      this.$emit('addRepairQuoteTemplateItems', this.templateToAdd.templateId)
    },
    cancelAddItems() {
      this.addTemplateConfirmModalActive = false
      this.templateToAdd = null
    },
    async getQuoteTemplates() {
      this.templatesLoading = true
      const quoteTemplates = await QuoteTemplateService.getQuoteTemplates(this.filter)
      const quoteRepairerTemplates = quoteTemplates.filter((template) => template.quoteType == this.quoteTemplateTypeKeys.Repairer)
      let quoteRepairerTemplatesWithoutLabour = []
      let quoteRepairerTemplatesWithLabour = []

      if (this.quoteTimesType === 'NTAR' || this.quoteTimesType === 'LTAR') {
        // For NTAR quotes - templates are tied to times/body style
        quoteRepairerTemplatesWithoutLabour = quoteRepairerTemplates.filter(
          (template) =>
            (template.timesType == null && template.bodyId == null) ||
            (template.timesType == null && template.bodyId === this.vehicleBodyId) ||
            (template.bodyId == null && this.matchingTimesType(this.quoteTimesType, template.timesType))
        )
        quoteRepairerTemplatesWithLabour = quoteRepairerTemplates.filter(
          (template) => template.bodyId === this.vehicleBodyId && this.matchingTimesType(this.quoteTimesType, template.timesType)
        )
      } else {
        // For non-NTAR quotes - no restrictions, users can use any templates
        quoteRepairerTemplatesWithoutLabour = quoteRepairerTemplates.filter((template) => template.timesType == null || template.bodyId == null)
        quoteRepairerTemplatesWithLabour = quoteRepairerTemplates.filter((template) => template.timesType != null && template.bodyId != null)
      }
      this.templates = [...quoteRepairerTemplatesWithLabour, ...quoteRepairerTemplatesWithoutLabour]
      this.templatesLoading = false
    },
    matchingTimesType(quoteTimesType, templateTimesType) {
      return (
        (quoteTimesType === 'LTAR' && templateTimesType === 'LTAR') ||
        (quoteTimesType === 'NTAR' && templateTimesType === 'NTAR') ||
        (quoteTimesType !== 'LTAR' && quoteTimesType !== 'NTAR' && templateTimesType && templateTimesType !== 'NTAR' && templateTimesType !== 'LTAR')
      )
    },
    handleClose() {
      this.isTemplateItemsModalActive = false
      this.selectedTemplate = null
    },
    toggleFilterDropdown() {
      this.isFilterActive = !this.isFilterActive
      if (this.isFilterActive) {
        document.addEventListener('click', this.handleClickOutsideFilter)
      } else {
        document.removeEventListener('click', this.handleClickOutsideFilter)
      }
    },
    handleClickOutsideFilter(event) {
      const dropdown = this.$el.querySelector('.dropdown')
      if (dropdown && !dropdown.contains(event.target)) {
        this.isFilterActive = false
        document.removeEventListener('click', this.handleClickOutsideFilter)
      }
    },
    handleSaveTags(tags) {
      this.$eventHub.$emit(EventHubTypes.ReloadQuoteTemplate)
    },
    toggleManageTagsModal() {
      this.showManageTagsModal = !this.showManageTagsModal
      if (this.showManageTagsModal) {
        this.isFilterActive = false
      }
    },
    toggleFilterTag(tag) {
      const idx = this.selectedFilterTags.indexOf(tag.tagID)
      if (idx === -1) {
        this.selectedFilterTags.push(tag.tagID)
      } else {
        this.selectedFilterTags.splice(idx, 1)
      }
    },
    clearAllFilterTags() {
      this.selectedFilterTags = []
    },
    getSortedTags(tags) {
      if (!tags || !Array.isArray(tags)) return []
      return [...tags].sort((a, b) => a.shortTag.localeCompare(b.shortTag))
    }
  }
}
</script>

<style lang="scss" scoped>
.templates-container {
  max-height: 70vh;
  overflow-y: auto;
}

.template-list-item {
  border-radius: 5px;
}

.tags-container {
  display: flex;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
